import React, { useState } from 'react';
import {
  BookmarkIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  useListBidScenarios,
  useDeleteBidScenario,
  useUpdateBidScenario,
} from '../../../hooks/bid_wizard/useBidScenarios';
import apiService from '../../../services/api';
import { formatCurrency } from '../utils/bidWizardHelpers';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const EngagementBadge = ({ type }) => {
  if (!type) return null;
  const isRetainer = type.toLowerCase() === 'retainer';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
      isRetainer ? 'bg-violet-100 text-violet-700' : 'bg-brightblue-100 text-brightblue-700'
    }`}>
      {isRetainer ? 'Retainer' : 'Project'}
    </span>
  );
};

// ─── Inline rename ────────────────────────────────────────────────────────────

const RenameInput = ({ initialValue, onSave, onCancel }) => {
  const [value, setValue] = useState(initialValue);
  return (
    <div className="flex items-center gap-1.5">
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  onSave(value.trim());
          if (e.key === 'Escape') onCancel();
        }}
        className="flex-1 px-2 py-1 text-sm border border-brightblue-400 rounded-lg outline-none focus:ring-2 focus:ring-brightblue-300 text-darkblue-500"
      />
      <button type="button" onClick={() => onSave(value.trim())} disabled={!value.trim()}
        className="p-1 text-teal-600 hover:text-teal-700 disabled:opacity-40">
        <CheckIcon className="w-4 h-4" />
      </button>
      <button type="button" onClick={onCancel} className="p-1 text-grey-400 hover:text-grey-600">
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// ─── Metric cell ──────────────────────────────────────────────────────────────

const Metric = ({ value, label, valueClass = 'text-darkblue-500' }) => (
  <div className="text-center">
    <p className={`text-sm font-bold tabular-nums ${valueClass}`}>{value}</p>
    <p className="text-[10px] text-grey-400 mt-0.5 whitespace-nowrap">{label}</p>
  </div>
);

// ─── Scenario card ────────────────────────────────────────────────────────────

const ScenarioCard = ({ scenario, onLoad, onDelete, onRename }) => {
  const [renaming,       setRenaming]       = useState(false);
  const [loadingFull,    setLoadingFull]    = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(false);

  const isRetainer     = scenario.engagement_type?.toLowerCase() === 'retainer';
  const computedMargin = scenario.bid_value && scenario.total_cost
    ? ((scenario.bid_value - scenario.total_cost) / scenario.bid_value) * 100
    : scenario.margin;

  const marginColor = computedMargin >= 40 ? 'text-teal-600'
    : computedMargin >= 25 ? 'text-yellow-600'
    : 'text-pink-500';

  const handleLoad = async () => {
    setLoadingFull(true);
    try {
      const full = await apiService.get(`/bid-scenarios/${scenario.id}`);
      if (full && onLoad) onLoad(full);
    } catch (err) {
      console.error('Failed to load scenario:', err);
    } finally {
      setLoadingFull(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-grey-200 hover:border-brightblue-300 hover:shadow-md transition-all flex flex-col overflow-hidden">

      {/* ── Colored top accent bar ── */}
      <div className={`h-1 w-full ${isRetainer ? 'bg-violet-500' : 'bg-brightblue-500'}`} />

      {/* ── Card body ── */}
      <div className="p-4 flex-1 space-y-3">

        {/* Header: name + actions */}
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {renaming ? (
              <RenameInput
                initialValue={scenario.name}
                onSave={(name) => { onRename(scenario.id, name); setRenaming(false); }}
                onCancel={() => setRenaming(false)}
              />
            ) : (
              <p className="text-sm font-bold text-darkblue-500 truncate leading-snug">
                {scenario.name}
              </p>
            )}
            {scenario.client_name && (
              <p className="text-xs text-grey-400 mt-0.5 truncate">{scenario.client_name}</p>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <EngagementBadge type={scenario.engagement_type} />
            <button type="button" onClick={() => setRenaming(true)}
              className="p-1.5 text-grey-300 hover:text-darkblue-500 rounded transition-colors">
              <PencilIcon className="w-3.5 h-3.5" />
            </button>
            {confirmDelete ? (
              <div className="flex items-center gap-1">
                <button type="button"
                  onClick={() => { onDelete(scenario.id); setConfirmDelete(false); }}
                  className="px-2 py-0.5 text-xs font-semibold text-white bg-pink-500 rounded hover:bg-pink-600">
                  Yes
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)}
                  className="px-2 py-0.5 text-xs font-semibold text-grey-500 bg-grey-100 rounded hover:bg-grey-200">
                  No
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)}
                className="p-1.5 text-grey-300 hover:text-pink-500 rounded transition-colors">
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Hero bid value */}
        <div className="bg-darkblue-500 rounded-lg px-3 py-2.5 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-darkblue-200 mb-0.5">
            {isRetainer ? 'Monthly Bid' : 'Total Bid'}
          </p>
          <p className="text-2xl font-bold tracking-tight">{formatCurrency(scenario.bid_value)}</p>
          {(scenario.min_bid > 0 || scenario.max_bid > 0) && (
            <p className="text-[10px] text-darkblue-300 mt-0.5">
              Range: {formatCurrency(scenario.min_bid)} – {formatCurrency(scenario.max_bid)}
            </p>
          )}
        </div>

        {/* Metrics row */}
        <div className="grid grid-cols-3 gap-2 bg-grey-50 rounded-lg px-3 py-2.5">
          <Metric
            value={scenario.total_hours != null ? `${Number(scenario.total_hours).toFixed(1)}h` : '—'}
            label={isRetainer ? 'hrs / mo' : 'total hrs'}
          />
          <Metric
            value={scenario.bid_value && scenario.total_hours
              ? formatCurrency(scenario.bid_value / scenario.total_hours)
              : '—'}
            label="blended rate"
          />
          <Metric
            value={computedMargin != null ? `${Number(computedMargin).toFixed(1)}%` : '—'}
            label="margin"
            valueClass={marginColor}
          />
        </div>

        {/* Notes */}
        {scenario.notes && (
          <p className="text-xs text-grey-500 italic line-clamp-2 leading-relaxed">
            {scenario.notes}
          </p>
        )}

        {/* Tags row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {scenario.selected_template?.engagement_name && (
            <span className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-600 rounded border border-teal-200 font-medium">
              Ref: {scenario.selected_template.engagement_name}
            </span>
          )}
          {scenario.selected_baseline?.label && (
            <span className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded border border-violet-200 font-medium">
              Baseline: {scenario.selected_baseline.label}
            </span>
          )}
          <span className="ml-auto flex items-center gap-1 text-[10px] text-grey-300">
            <ClockIcon className="w-3 h-3" />
            {fmtDate(scenario.updated_at)}
          </span>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="px-4 py-3 border-t border-grey-100 bg-grey-50 flex justify-end">
        <button
          type="button"
          onClick={handleLoad}
          disabled={loadingFull}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-darkblue-500 rounded-lg hover:bg-darkblue-600 disabled:opacity-60 transition-colors"
        >
          {loadingFull
            ? <><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />Loading…</>
            : <><ArrowUpTrayIcon className="w-3.5 h-3.5" />Load</>
          }
        </button>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SavedBidsTab = ({ onLoadScenario, onNewBid }) => {
  const [filter, setFilter] = useState('all');

  const { data, isLoading, isError, refetch, isFetching } = useListBidScenarios({
    engagementType: filter === 'all' ? undefined : filter,
  });

  const deleteMut = useDeleteBidScenario();
  const updateMut = useUpdateBidScenario();

  const scenarios = data?.scenarios ?? [];
  const total     = data?.total     ?? 0;

  const retainerCount = scenarios.filter(s => s.engagement_type?.toLowerCase() === 'retainer').length;
  const projectCount  = scenarios.filter(s => s.engagement_type?.toLowerCase() === 'project').length;

  const handleDelete = (scenarioId) => deleteMut.mutate(scenarioId);
  const handleRename = (scenarioId, name) => { if (!name) return; updateMut.mutate({ scenarioId, name }); };

  return (
    <div className="space-y-4">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-darkblue-500">Saved Bids</h2>
          <p className="text-sm text-grey-500 mt-0.5">
            {isLoading ? 'Loading…' : `${total} saved scenario${total !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter pills */}
          <div className="flex items-center bg-grey-100 rounded-lg p-0.5 gap-0.5">
            {['all', 'retainer', 'project'].map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors capitalize ${
                  filter === f
                    ? 'bg-white text-darkblue-500 shadow-sm'
                    : 'text-grey-400 hover:text-darkblue-500'
                }`}>
                {f}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button type="button" onClick={() => refetch()} disabled={isFetching}
            className="p-1.5 text-grey-400 hover:text-darkblue-500 transition-colors rounded-lg border border-grey-200 disabled:opacity-50"
            title="Refresh">
            <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          {/* Counts */}
          <div className="flex items-center gap-3 pl-3 border-l border-grey-200">
            <div className="text-right">
              <div className="text-xl font-bold text-brand-500">{total}</div>
              <div className="text-[10px] text-grey-400 leading-none">Total</div>
            </div>
            {retainerCount > 0 && (
              <div className="text-right">
                <div className="text-xl font-bold text-violet-600">{retainerCount}</div>
                <div className="text-[10px] text-grey-400 leading-none">Retainer</div>
              </div>
            )}
            {projectCount > 0 && (
              <div className="text-right">
                <div className="text-xl font-bold text-brightblue-600">{projectCount}</div>
                <div className="text-[10px] text-grey-400 leading-none">Project</div>
              </div>
            )}
          </div>

          {onNewBid && (
            <button type="button" onClick={onNewBid}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors">
              <PlusIcon className="w-3.5 h-3.5" />New Bid
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-grey-400 gap-2">
          <ArrowPathIcon className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading scenarios…</span>
        </div>
      ) : isError ? (
        <div className="py-16 text-center text-sm text-pink-500">
          Failed to load scenarios.{' '}
          <button type="button" onClick={() => refetch()} className="underline">Retry</button>
        </div>
      ) : scenarios.length === 0 ? (
        <div className="py-16 text-center">
          <BookmarkIcon className="w-12 h-12 text-grey-200 mx-auto mb-3" />
          <p className="text-sm font-semibold text-darkblue-500">No saved bids yet</p>
          <p className="text-xs text-grey-400 mt-1">Build a bid in the wizard and click Save to store it here.</p>
          {onNewBid && (
            <button type="button" onClick={onNewBid}
              className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-brand-500 rounded-lg hover:bg-brand-600">
              Start New Bid
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((s) => (
            <ScenarioCard
              key={s.id}
              scenario={s}
              onLoad={onLoadScenario}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedBidsTab;